import { parseDate } from 'shared-components';
import orderBy from 'lodash/orderBy';
import { UserRole } from '../../store/auth/types';
import { hasPerm, P } from '../roles/permissions';
import {
  VisaStatus,
  OfferStatus,
  TravelStatusEnum,
  EventInfoStatus,
  IStudentRow,
  AccommodationArrangedStatusEnum,
  AccommodationTypeStatusEnum,
  RecordedInterviewSystemStatus,
} from 'api-paths-cas';
import { MaturityStatus } from '../../types';
import { getDurationFromDate } from '../helpers';
import { getLateArrivals } from '../../utilities/getLateArrivals';

type SortRuleSet = Array<{
  val: (student: IStudentRow) => any;
  reverse?: boolean;
}>;

// In sorting function, allow any value for temporary fields used by orderBy
interface IStudentRowFree extends IStudentRow {
  [key: string]: any;
}

const getPrimaryLastTouchSort = (lastTouchTime: string) => {
  if (lastTouchTime === 'New') {
    return 5;
  }
  const durationMeasurement = lastTouchTime.substring(lastTouchTime.length - 1);
  switch (durationMeasurement) {
    case 'm': {
      return 1;
    }
    case 'h': {
      return 2;
    }
    case 'd': {
      return 3;
    }
    case 'w': {
      return 4;
    }
    default: {
      return 5;
    }
  }
};

const getSecondaryLastTouchSort = (lastTouchTime: string) => {
  if (lastTouchTime === 'New') {
    return 0;
  }
  const durationMeasurement = lastTouchTime.substring(0, lastTouchTime.length - 1);
  return Number(durationMeasurement);
};

const getVisaStatusSort = (visaStatus: string | undefined): number => {
  if (visaStatus === VisaStatus.NONE || !visaStatus) {
    return 10;
  }
  switch (visaStatus) {
    case VisaStatus.CAS_NOT_RECEIVED: {
      return 1;
    }
    case VisaStatus.VISA_RECEIVED: {
      return 2;
    }
    case VisaStatus.INTENDS_TO_DEFER: {
      return 3;
    }
    case VisaStatus.INTENDS_TO_WITHDRAW: {
      return 4;
    }
    case VisaStatus.VISA_REFUSAL: {
      return 5;
    }
    case VisaStatus.UKVI_REQUEST_MORE_INFO: {
      return 6;
    }
    case VisaStatus.AWAITING_VISA_RESPONSE: {
      return 7;
    }
    case VisaStatus.VISA_APPLIED: {
      return 8;
    }
    case VisaStatus.CAS_RECEIVED: {
      return 9;
    }
    default: {
      return 10;
    }
  }
};

const getConditionalStatusSort = (conditionalStatus: string | undefined): number => {
  if (!conditionalStatus) {
    return 7;
  }
  switch (conditionalStatus) {
    case OfferStatus.FOR_REVIEW: {
      return 1;
    }
    case OfferStatus.NOT_RESOLVED: {
      return 2;
    }
    case OfferStatus.CONDITIONAL: {
      return 3;
    }
    case OfferStatus.UNCONDITIONAL: {
      return 4;
    }
    case OfferStatus.REMOVED: {
      return 5;
    }
    case OfferStatus.DECLINED: {
      return 6;
    }
    default: {
      return 7;
    }
  }
};

const getMaturitySort1 = (status: MaturityStatus) => {
  if (status.value === 'Approved') {
    return 1;
  }
  if (status.value === 'N/A') {
    return 2;
  }
  if (status.type === 'OK' && status.value === 'Mature') {
    return 3;
  }
  if (status.type === 'WARNING' && status.value === 'Mature') {
    return 4;
  }
  if (status.value === 'Insufficient') {
    return 5;
  }
  if (status.value === 'Inconclusive') {
    return 6;
  }
  if (status.value === 'TBD') {
    return 8;
  }
  // Check if value is a valid date string (DD/MM/YYYY format)
  try {
    const dateFormatRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    if (dateFormatRegex.test(status.value)) {
      return 7;
    }
    // Try parsing as ISO date
    const parsedDate = parseDate(status.value);
    if (parsedDate !== null) {
      return 7;
    }
  } catch (error) {
    // If parsing fails, continue to fallback
  }
  // fallback
  return 9;
};

// Sort earlier Maturity dates first
const getMaturitySort2 = (status: MaturityStatus) => {
  // Handle special cases that can't be parsed as dates
  if (
    status.value === 'N/A' ||
    status.value === 'TBD' ||
    status.value === 'Approved' ||
    status.value === 'Mature' ||
    status.value === 'Insufficient' ||
    status.value === 'Inconclusive'
  ) {
    return 0;
  }

  try {
    let date = null;

    // Check if it's DD/MM/YYYY format
    const dateFormatRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = status.value.match(dateFormatRegex);

    if (match) {
      // Convert DD/MM/YYYY to ISO format for parsing
      const [, day, month, year] = match;
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      date = parseDate(isoDate);
    } else {
      // Try parsing as-is (ISO format)
      date = parseDate(status.value);
    }

    if (date !== null) {
      // Convert PlainDate to milliseconds for sorting (using epoch milliseconds from start of day)
      return date.toZonedDateTime('UTC').toInstant().epochMilliseconds;
    }
  } catch (error) {
    // If parsing fails, return 0 to maintain stable sorting
    return 0;
  }
  return 0;
};

// Sort Maturity in order it is displayed in.
// Order is reversed - from "least ready" to "most ready".
export const sortMaturity = (fundsMatured: MaturityStatus[]): MaturityStatus[] => {
  const maturityCopy: any = fundsMatured.slice(0);
  maturityCopy.forEach((status: any) => {
    status['sortKey_1'] = getMaturitySort1(status);
    status['sortKey_2'] = getMaturitySort2(status);
  });
  const maturitySorted = orderBy(maturityCopy, ['sortKey_1', 'sortKey_2']).reverse();
  maturitySorted.forEach((status: any) => {
    delete status['sortKey_1'];
    delete status['sortKey_2'];
  });
  return maturitySorted;
};

const getScheduledInterviewsSort = (infoStatus?: EventInfoStatus): number => {
  const statusesByPriority = [
    EventInfoStatus.INVITED,
    EventInfoStatus.BOOKED,
    EventInfoStatus.FAILED,
    EventInfoStatus.PASSED,
    EventInfoStatus.RESIT,
    EventInfoStatus.NONE,
  ];

  const status = infoStatus || EventInfoStatus.NONE;

  return statusesByPriority.indexOf(status);
};

const getRecordedInterviewsSort = (recordedStatus?: RecordedInterviewSystemStatus): number => {
  const statusesByPriority = [
    RecordedInterviewSystemStatus.INVITED,
    RecordedInterviewSystemStatus.RECORDED,
    RecordedInterviewSystemStatus.FAILED,
    RecordedInterviewSystemStatus.PASSED,
    RecordedInterviewSystemStatus.RESIT,
    RecordedInterviewSystemStatus.ATTEMPTED,
    RecordedInterviewSystemStatus.NONE,
  ];

  const status = recordedStatus || RecordedInterviewSystemStatus.NONE;

  return statusesByPriority.indexOf(status);
};

const reverseDir = (dir: 'ASC' | 'DESC') => {
  return dir === 'ASC' ? 'DESC' : 'ASC';
};

const customRuleSets: {
  [key: string]: SortRuleSet;
} = {
  student_number: [
    {
      val: student => Number(student.student_number),
    },
  ],
  surname: [
    {
      val: student => student.surname && student.surname.toLowerCase(),
    },
  ],
  given_name: [
    {
      val: student => student.given_name && student.given_name.toLowerCase(),
    },
  ],
  course: [
    {
      val: student => student.course && student.course.toLowerCase(),
    },
  ],
  questionComplete: [
    {
      val: student => Number(student.questionComplete.split('%')[0]) || 0,
      reverse: true,
    },
  ],
  isTasksComplete: [
    {
      val: student => {
        const maxTime = 9999999999999; // static max date gettime
        if (student.isTasksComplete) {
          return (student.tasksCompleteAt ?? 0) === 0 ? maxTime : (student.tasksCompleteAt ?? maxTime);
        }
        return maxTime;
      },
    },
  ],
  documentComplete: [
    {
      val: student => {
        const s = student.documentComplete.split('/');
        const uploaded = Number(s[0]) || 0;
        const total = Number(s[1]) || 0;
        return total === 0 ? 0 : uploaded / total;
      },
      reverse: true,
    },
  ],
  agentDocumentComplete: [
    {
      val: student => {
        const s = student.agentDocumentComplete.split('/');
        const uploaded = Number(s[0]) || 0;
        const total = Number(s[1]) || 0;
        return total === 0 ? 0 : uploaded / total;
      },
      reverse: true,
    },
  ],
  successPrediction: [
    {
      val: student => Number(student.successPrediction.split('%')[0]) || 0,
      reverse: true,
    },
  ],
  fundsMatured: [
    {
      val: student => {
        if (student.fundsMatured.length > 0) {
          const first = sortMaturity(student.fundsMatured)[0];
          return getMaturitySort1(first);
        }
        return undefined;
      },
    },
    {
      val: student => {
        if (student.fundsMatured.length > 0) {
          const first = sortMaturity(student.fundsMatured)[0];
          return getMaturitySort2(first);
        }
        return undefined;
      },
    },
    {
      val: student => {
        // Sort less Maturity sources first
        return student.fundsMatured.length;
      },
    },
  ],
  expectedDepositDate: [{ val: student => getDurationFromDate(student.expectedDepositDate ?? null) }],
  lastTouch: [
    {
      val: student => getPrimaryLastTouchSort(student.lastTouch),
    },
    {
      val: student => getSecondaryLastTouchSort(student.lastTouch),
    },
  ],
  offerStatus: [{ val: student => getConditionalStatusSort(student.offerStatus) }],
  travelStatus: [
    // RED
    {
      val: student => student.travelStatus === TravelStatusEnum.TravelCancelled,
    },
    {
      val: student => student.travelStatus === TravelStatusEnum.TravelPrevented,
    },
    {
      val: student => student.travelStatus === TravelStatusEnum.Defer,
    },
    {
      val: student => student.travelStatus === TravelStatusEnum.Withdraw,
    },
    {
      val: ({ travelBookedDate, lastAcceptanceDate = '', travelStatus }) => {
        const isRed = getLateArrivals(travelBookedDate, lastAcceptanceDate);
        return travelStatus === TravelStatusEnum.Booked && isRed;
      },
    },
    // BLUE
    {
      val: ({ travelBookedDate, lastAcceptanceDate = '', travelStatus }) => {
        const isRed = getLateArrivals(travelBookedDate, lastAcceptanceDate);
        return travelStatus === TravelStatusEnum.Booked && !isRed;
      },
    },
    // GREEN
    {
      val: student => [TravelStatusEnum.Arrived, TravelStatusEnum.AlreadyInUk].includes(student.travelStatus),
    },
    // GRAY
    {
      val: student => student.travelStatus === TravelStatusEnum.NoBookingYet,
    },
    {
      val: student => student.travelStatus === TravelStatusEnum.None,
    },
  ],
  unreadMessagesCount: [
    {
      val: student => (isNaN(Number(student.unreadMessagesCount)) ? 0 : Number(student.unreadMessagesCount)),
    },
  ],
  visaStatus: [
    {
      val: student => getVisaStatusSort(student.visaStatus),
    },
  ],
  scheduledInterviews: [
    {
      val: student => getScheduledInterviewsSort((student.calendlyInterviewsInfoStatus || '') as EventInfoStatus),
    },
  ],
  RecordedInterviews: [
    {
      val: student =>
        getRecordedInterviewsSort((student.recordedInterviewSystemStatus || '') as RecordedInterviewSystemStatus),
    },
  ],
  accommodationStatus: [
    {
      val: student => student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NONE,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.I_DONT_KNOW,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.HOMESTAY,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.STAYING_WITH_FAMILY,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.PRIVATE_RENTAL,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.PRIVATE_STUDENT_ACCOMMODATION,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.UNIVERSITY_ACCOMMODATION,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.OTHER,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.HOMESTAY,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.STAYING_WITH_FAMILY,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.PRIVATE_RENTAL,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.PRIVATE_STUDENT_ACCOMMODATION,
    },
    {
      val: student =>
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.ARRANGED &&
        student.accommodationTypeStatus === AccommodationTypeStatusEnum.UNIVERSITY_ACCOMMODATION,
    },
  ],
};

const sortStudents = (
  baseStudents: IStudentRow[],
  column: string,
  order: 'ASC' | 'DESC',
  role: UserRole,
): IStudentRow[] => {
  let students: IStudentRowFree[] = baseStudents.map(student => ({
    ...student,
  }));
  const sortKeys = [];
  const tempSortKeys: string[] = [];
  const sortOrders: ('ASC' | 'DESC')[] = [];
  const ruleSets: SortRuleSet[] = [];

  switch (column) {
    case 'SORT_BY_TASK_COMPLETE': {
      ruleSets.push(customRuleSets.isTasksComplete);
      ruleSets.push(customRuleSets.questionComplete);
      ruleSets.push(customRuleSets.documentComplete);
      break;
    }
    case 'documentComplete': {
      const sortByLastTouch = hasPerm(role, P.LAST_TOUCH_VIEW);
      ruleSets.push(customRuleSets.documentComplete);
      if (sortByLastTouch) {
        ruleSets.push(customRuleSets.lastTouch);
      } else {
        ruleSets.push(customRuleSets.fundsMatured);
      }

      break;
    }
    default:
      if (customRuleSets[column]) {
        ruleSets.push(customRuleSets[column]);
      } else {
        sortKeys.push(column);
        sortOrders.push(order);
      }

      break;
  }

  // last priority sort by surname
  if (!ruleSets.includes(customRuleSets.surname)) {
    ruleSets.push(customRuleSets.surname);
  }

  let i = 0;
  ruleSets.forEach(ruleSet => {
    ruleSet.forEach(rule => {
      const newSortKey = `sortKey_${++i}`;
      sortKeys.push(newSortKey);
      tempSortKeys.push(newSortKey);
      sortOrders.push(rule.reverse ? reverseDir(order) : order);
      students.forEach(student => {
        student[newSortKey] = rule.val(student);
      });
    });
  });

  students = orderBy(
    students,
    sortKeys,
    sortOrders.map(order => order.toLowerCase() as 'asc' | 'desc'),
  ) as IStudentRowFree[];

  students.forEach(student => {
    tempSortKeys.forEach(col => {
      delete student[col];
    });
  });

  return students;
};

export default sortStudents;
